import React, { useContext, useState, useEffect, useMemo } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import { getDownloadExtension } from '../utils/downloadFilename';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Upload, Download, Check, User, Trash2 } from 'lucide-react';
import ProfilePhotoCropModal from '../components/ProfilePhotoCropModal';
import AuthenticatedImage from '../components/AuthenticatedImage';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = rawBackendUrl ? rawBackendUrl.replace(/\/$/, '') : 'http://api.acbase.org';
const getBackendMediaUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}/api/${path}`;
};

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeSelectValue = (value) => String(value ?? '').trim();

// Lista prefissi internazionali con bandierine
const PHONE_PREFIXES = [
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'Regno Unito', flag: '🇬🇧' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+49', country: 'Germania', flag: '🇩🇪' },
  { code: '+34', country: 'Spagna', flag: '🇪🇸' },
  { code: '+41', country: 'Svizzera', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+31', country: 'Paesi Bassi', flag: '🇳🇱' },
  { code: '+32', country: 'Belgio', flag: '🇧🇪' },
  { code: '+351', country: 'Portogallo', flag: '🇵🇹' },
  { code: '+30', country: 'Grecia', flag: '🇬🇷' },
  { code: '+93', country: 'Afghanistan', flag: '🇦🇫' },
  { code: '+355', country: 'Albania', flag: '🇦🇱' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+244', country: 'Angola', flag: '🇦🇴' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+374', country: 'Armenia', flag: '🇦🇲' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+994', country: 'Azerbaigian', flag: '🇦🇿' },
  { code: '+973', country: 'Bahrein', flag: '🇧🇭' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+375', country: 'Bielorussia', flag: '🇧🇾' },
  { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '+387', country: 'Bosnia-Erzegovina', flag: '🇧🇦' },
  { code: '+267', country: 'Botswana', flag: '🇧🇼' },
  { code: '+55', country: 'Brasile', flag: '🇧🇷' },
  { code: '+673', country: 'Brunei', flag: '🇧🇳' },
  { code: '+359', country: 'Bulgaria', flag: '🇧🇬' },
  { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
  { code: '+257', country: 'Burundi', flag: '🇧🇮' },
  { code: '+855', country: 'Cambogia', flag: '🇰🇭' },
  { code: '+237', country: 'Camerun', flag: '🇨🇲' },
  { code: '+238', country: 'Capo Verde', flag: '🇨🇻' },
  { code: '+236', country: 'Repubblica Centrafricana', flag: '🇨🇫' },
  { code: '+235', country: 'Ciad', flag: '🇹🇩' },
  { code: '+56', country: 'Cile', flag: '🇨🇱' },
  { code: '+86', country: 'Cina', flag: '🇨🇳' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+269', country: 'Comore', flag: '🇰🇲' },
  { code: '+242', country: 'Congo', flag: '🇨🇬' },
  { code: '+243', country: 'Repubblica Democratica del Congo', flag: '🇨🇩' },
  { code: '+225', country: 'Costa d’Avorio', flag: '🇨🇮' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '+53', country: 'Cuba', flag: '🇨🇺' },
  { code: '+357', country: 'Cipro', flag: '🇨🇾' },
  { code: '+45', country: 'Danimarca', flag: '🇩🇰' },
  { code: '+20', country: 'Egitto', flag: '🇪🇬' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '+291', country: 'Eritrea', flag: '🇪🇷' },
  { code: '+372', country: 'Estonia', flag: '🇪🇪' },
  { code: '+251', country: 'Etiopia', flag: '🇪🇹' },
  { code: '+679', country: 'Figi', flag: '🇫🇯' },
  { code: '+358', country: 'Finlandia', flag: '🇫🇮' },
  { code: '+220', country: 'Gambia', flag: '🇬🇲' },
  { code: '+995', country: 'Georgia', flag: '🇬🇪' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '+224', country: 'Guinea', flag: '🇬🇳' },
  { code: '+240', country: 'Guinea Equatoriale', flag: '🇬🇶' },
  { code: '+592', country: 'Guyana', flag: '🇬🇾' },
  { code: '+509', country: 'Haiti', flag: '🇭🇹' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳' },
  { code: '+852', country: 'Hong Kong', flag: '🇭🇰' },
  { code: '+36', country: 'Ungheria', flag: '🇭🇺' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+98', country: 'Iran', flag: '🇮🇷' },
  { code: '+964', country: 'Iraq', flag: '🇮🇶' },
  { code: '+353', country: 'Irlanda', flag: '🇮🇪' },
  { code: '+972', country: 'Israele', flag: '🇮🇱' },
  { code: '+81', country: 'Giappone', flag: '🇯🇵' },
  { code: '+962', country: 'Giordania', flag: '🇯🇴' },
  { code: '+7', country: 'Federazione Russa', flag: '🇷🇺' },
  { code: '+90', country: 'Turchia', flag: '🇹🇷' },
  { code: '+380', country: 'Ucraina', flag: '🇺🇦' },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' }
];

export default function ProfilePage() {
  const { user, setUser } = useContext(AuthContext);
  
  // Profile data
  const [profileData, setProfileData] = useState({
    phone: '',
    phone_prefix: '+39',
    country_id: null,
    province_id: null,
    city_id: null,
    city_strgin: '',
    company_name: '',
    job_title: '',
    job_category_id: null,
    company_sector_id: null,
    linkedin_url: '',
    additional_info: ''
  });
  
  const [cvFile, setCvFile] = useState(null);
  const [hasCv, setHasCv] = useState(false);
  const [motivationLetterFile, setMotivationLetterFile] = useState(null);
  const [hasMotivationLetter, setHasMotivationLetter] = useState(false);
  const [hasAdhesionLetter, setHasAdhesionLetter] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [profilePhotoVersion, setProfilePhotoVersion] = useState(0);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawProfilePhotoFile, setRawProfilePhotoFile] = useState(null);
  
  // Phone change with OTP
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newPhonePrefix, setNewPhonePrefix] = useState('+39');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  
  // Email change with OTP
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [consents, setConsents] = useState([]);
  const [consentValues, setConsentValues] = useState({});
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [savingConsents, setSavingConsents] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  
  // Reference data
  const [countries, setCountries] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [companySectors, setCompanySectors] = useState([]);

  const countryOptions = useMemo(
    () =>
      (countries || []).map((country) => ({
        ...country,
        idValue: normalizeSelectValue(country.id)
      })),
    [countries]
  );

  const selectedCountryValue = useMemo(() => {
    const currentValue = normalizeSelectValue(profileData.country_id);
    if (!currentValue) {
      return '';
    }
    return countryOptions.some((country) => country.idValue === currentValue) ? currentValue : '';
  }, [profileData.country_id, countryOptions]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        phone: user.phone || '',
        phone_prefix: user.phone_prefix || '+39',
        country_id: toNullableNumber(user.country_id),
        province_id: toNullableNumber(user.province_id),
        city_id: toNullableNumber(user.city_id),
        city_strgin: user.city_strgin || user.city || '',
        company_name: user.company_name || '',
        job_title: user.job_title || '',
        job_category_id: toNullableNumber(user.job_category_id),
        company_sector_id: toNullableNumber(user.company_sector_id),
        linkedin_url: user.linkedin_url || '',
        additional_info: user.additional_info || ''
      });
      setHasCv(!!user.cv_path);
      setHasMotivationLetter(!!user.motivation_letter_path);
      setHasAdhesionLetter(!!user.adhesion_letter_path);
      setProfilePhotoUrl(user.profile_photo_url || '');
      setProfilePhotoVersion(Date.now());
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchMyConsents();
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      fetchMyQuotes();
      return;
    }
    setQuotes([]);
  }, [user?.id]);

  const fetchReferenceData = async () => {
    try {
      const [countriesRes, jobCatRes, compSecRes] = await Promise.all([
        axios.get('/countries'),
        axios.get('/job-categories'),
        axios.get('/company-sectors')
      ]);
      setCountries(countriesRes.data);
      setJobCategories(jobCatRes.data);
      setCompanySectors(compSecRes.data);
    } catch (error) {
      console.error('Failed to fetch reference data', error);
    }
  };

  const cityValue = (profileData.city_strgin || '').trim();
  const companyNameValue = (profileData.company_name || '').trim();
  const jobTitleValue = (profileData.job_title || '').trim();

  const isPersonalTabComplete = !!profileData.country_id && !!cityValue;
  const isProfessionalTabComplete =
    !!companyNameValue &&
    !!jobTitleValue &&
    !!profileData.job_category_id &&
    !!profileData.company_sector_id;

  const handleProfileUpdate = async (e, section = 'personal') => {
    e.preventDefault();
    
    if (saving) {
      return;
    }

    let payload = {};

    if (section === 'personal') {
      if (!isPersonalTabComplete) {
        toast.error('Compila Paese e Città prima di salvare');
        return;
      }

      payload = {
        country_id: profileData.country_id,
        province_id: profileData.province_id,
        city_id: profileData.city_id,
        city_strgin: cityValue
      };
    } else if (section === 'professional') {
      if (!isProfessionalTabComplete) {
        toast.error('Compila tutti i campi obbligatori delle informazioni professionali');
        return;
      }

      payload = {
        company_name: companyNameValue,
        job_title: jobTitleValue,
        job_category_id: profileData.job_category_id,
        company_sector_id: profileData.company_sector_id,
        linkedin_url: (profileData.linkedin_url || '').trim(),
        additional_info: profileData.additional_info || ''
      };
    } else {
      return;
    }
    
    setSaving(true);
    try {
      console.log('Saving profile data:', payload);
      
      // Save profile data
      const updateResponse = await axios.put('/profile/update', payload);
      console.log('Profile update response:', updateResponse.status, updateResponse.data);
      
      // Refresh user data from server
      const userRes = await axios.get('/auth/me');
      console.log('User data refreshed:', userRes.data);
      setUser(userRes.data);
      
      // Update form data with refreshed values to reflect changes
      setProfileData({
        phone: userRes.data.phone || '',
        phone_prefix: userRes.data.phone_prefix || '+39',
        country_id: toNullableNumber(userRes.data.country_id),
        province_id: toNullableNumber(userRes.data.province_id),
        city_id: toNullableNumber(userRes.data.city_id),
        city_strgin: userRes.data.city_strgin || userRes.data.city || '',
        company_name: userRes.data.company_name || '',
        job_title: userRes.data.job_title || '',
        job_category_id: toNullableNumber(userRes.data.job_category_id),
        company_sector_id: toNullableNumber(userRes.data.company_sector_id),
        linkedin_url: userRes.data.linkedin_url || '',
        additional_info: userRes.data.additional_info || ''
      });
      
      toast.success('Profilo aggiornato con successo');
    } catch (error) {
      console.error('Failed to update profile', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Request data:', payload);
      
      // Show specific error message from backend
      const errorMessage = getErrorMessage(error, 'Errore nell\'aggiornamento del profilo');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const fetchMyConsents = async () => {
    setLoadingConsents(true);
    try {
      const response = await axios.get('/consents/me');
      const consentList = response.data?.consents || [];
      const values = {};
      consentList.forEach((consent) => {
        values[consent.id] = !!consent.accepted;
      });
      setConsents(consentList);
      setConsentValues(values);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore caricamento preferenze'));
    } finally {
      setLoadingConsents(false);
    }
  };

  const fetchMyQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const response = await axios.get('/quote-associative/me');
      setQuotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore caricamento quote associative'));
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleSaveConsents = async () => {
    if (!consents.length) {
      return;
    }
    setSavingConsents(true);
    try {
      await axios.put('/consents/me', {
        consents: consents.map((consent) => ({
          consent_id: consent.id,
          accepted: !!consentValues[consent.id]
        }))
      });
      toast.success('Preferenze notifiche aggiornate');
      await fetchMyConsents();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore salvataggio preferenze'));
    } finally {
      setSavingConsents(false);
    }
  };

  const handleCountryChange = (countryId) => {
    const parsedCountryId = toNullableNumber(countryId);
    setProfileData(prev => ({ 
      ...prev, 
      country_id: parsedCountryId,
      province_id: null, 
      city_id: null 
    }));
  };

  // Phone OTP flow
  const handleRequestPhoneOtp = async () => {
    if (!newPhone || newPhone.length < 6) {
      toast.error('Inserisci un numero di telefono valido');
      return;
    }
    
    setSendingPhoneOtp(true);
    try {
      const res = await axios.post('/members/phone/request-otp', {
        phone: newPhone,
        phone_prefix: newPhonePrefix
      });
      setPhoneOtpSent(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'invio OTP'));
    } finally {
      setSendingPhoneOtp(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!phoneOtpCode || phoneOtpCode.length !== 6) {
      toast.error('Inserisci un codice OTP valido a 6 cifre');
      return;
    }
    
    setVerifyingPhone(true);
    try {
      await axios.post('/members/phone/verify-otp', {
        phone: newPhone,
        phone_prefix: newPhonePrefix,
        otp_code: phoneOtpCode
      });
      toast.success('Telefono aggiornato con successo!');
      setIsPhoneModalOpen(false);
      setPhoneOtpSent(false);
      setPhoneOtpCode('');
      setNewPhone('');
      
      // Refresh user data
      const userRes = await axios.get('/auth/me');
      setUser(userRes.data);
      setProfileData(prev => ({
        ...prev,
        phone: userRes.data.phone,
        phone_prefix: userRes.data.phone_prefix
      }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'OTP non valido'));
    } finally {
      setVerifyingPhone(false);
    }
  };

  // Email OTP flow
  const handleRequestEmailOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newEmail || !emailRegex.test(newEmail)) {
      toast.error('Inserisci un\'email valida');
      return;
    }
    
    setSendingEmailOtp(true);
    try {
      const res = await axios.post('/members/email/request-otp', {
        email: newEmail
      });
      setEmailOtpSent(true);
      toast.success(res.data.message);
      // Per sviluppo mostra OTP
      if (res.data.debug_otp) {
        toast.info(`OTP di test: ${res.data.debug_otp}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'invio OTP'));
    } finally {
      setSendingEmailOtp(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtpCode || emailOtpCode.length !== 6) {
      toast.error('Inserisci un codice OTP valido a 6 cifre');
      return;
    }
    
    setVerifyingEmail(true);
    try {
      await axios.post('/members/email/verify-otp', {
        email: newEmail,
        otp_code: emailOtpCode
      });
      toast.success('Email aggiornata con successo!');
      setIsEmailModalOpen(false);
      setEmailOtpSent(false);
      setEmailOtpCode('');
      setNewEmail('');
      
      // Refresh user data
      const userRes = await axios.get('/auth/me');
      setUser(userRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'OTP non valido'));
    } finally {
      setVerifyingEmail(false);
    }
  };

  // CV upload
  const handleCvUpload = async () => {
    if (!cvFile) {
      toast.error('Seleziona un file');
      return;
    }
    
    const formData = new FormData();
    formData.append('cv', cvFile);
    
    setSaving(true);
    try {
      await axios.post(`/members/${user.id}/cv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('CV caricato con successo!');
      setHasCv(true);
      setCvFile(null);
      
      // Refresh user data
      const userRes = await axios.get('/auth/me');
      setUser(userRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel caricamento del CV'));
    } finally {
      setSaving(false);
    }
  };

  const handleCvDownload = async () => {
    try {
      const response = await axios.get(`/members/${user.id}/cv`, {
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: user.cv_path,
        contentDisposition: response.headers?.['content-disposition']
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CV_${user.first_name}_${user.last_name}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nel download del CV');
    }
  };

  const handleMotivationLetterUpload = async () => {
    if (!motivationLetterFile) {
      toast.error('Seleziona un file');
      return;
    }

    const formData = new FormData();
    formData.append('file', motivationLetterFile);

    setSaving(true);
    try {
      await axios.post('/members/motivation-letter/me', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Lettera motivazionale caricata con successo');
      setMotivationLetterFile(null);
      setHasMotivationLetter(true);

      const userRes = await axios.get('/auth/me');
      setUser(userRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel caricamento della lettera motivazionale'));
    } finally {
      setSaving(false);
    }
  };

  const handleMotivationLetterDownload = async () => {
    try {
      const response = await axios.get(`/members/${user.id}/motivation-letter`, {
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: user.motivation_letter_path,
        contentDisposition: response.headers?.['content-disposition']
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lettera_Motivazionale_${user.first_name}_${user.last_name}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel download della lettera motivazionale'));
    }
  };

  const handleAdhesionLetterDownload = async () => {
    try {
      const response = await axios.get(`/members/${user.id}/adhesion-letter`, {
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: user.adhesion_letter_path,
        contentDisposition: response.headers?.['content-disposition']
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lettera_Adesione_${user.first_name}_${user.last_name}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel download della lettera di adesione'));
    }
  };

  const handleProfilePhotoUpload = async (fileToUpload) => {
    if (!fileToUpload) {
      toast.error('File foto non valido');
      return;
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);

    setSaving(true);
    try {
      await axios.post('/members/profile-photo/me', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Foto profilo aggiornata');

      const userRes = await axios.get('/auth/me');
      setUser(userRes.data);
      setProfilePhotoUrl(userRes.data.profile_photo_url || '');
      setProfilePhotoVersion(Date.now());
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel caricamento della foto'));
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePhotoDelete = async () => {
    if (!window.confirm('Rimuovere la foto profilo?')) {
      return;
    }

    setSaving(true);
    try {
      await axios.delete('/members/profile-photo/me');
      toast.success('Foto profilo rimossa');
      setProfilePhotoUrl('');
      setProfilePhotoVersion(Date.now());

      const userRes = await axios.get('/auth/me');
      setUser(userRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella rimozione della foto'));
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePhotoInputChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = '';
    if (!selectedFile) {
      return;
    }
    setRawProfilePhotoFile(selectedFile);
    setCropModalOpen(true);
  };

  const handleCropConfirm = async (croppedFile) => {
    setRawProfilePhotoFile(null);
    setCropModalOpen(false);
    await handleProfilePhotoUpload(croppedFile);
  };

  const formatQuoteDate = (value) => {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleDateString('it-IT');
  };

  const formatQuoteAmount = (amount) => {
    const numericAmount = Number(amount || 0);
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(Number.isFinite(numericAmount) ? numericAmount : 0);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="mx-auto max-w-7xl px-4 pb-6 pt-20 sm:px-6">
        <div className="mb-6">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <User className="h-6 w-6" />
            <h1 className="text-3xl font-bold">Il mio profilo</h1>
          </div>
        </div>

        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-1 gap-1 sm:grid-cols-2 xl:grid-cols-5">
            <TabsTrigger value="personal" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
              Informazioni personali
            </TabsTrigger>
            <TabsTrigger value="professional" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
              Informazioni professionali
            </TabsTrigger>
            <TabsTrigger value="documents" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
              Documenti
            </TabsTrigger>
            <TabsTrigger value="quotes" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
              Quote associative
            </TabsTrigger>
            <TabsTrigger value="privacy" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
              Privacy e Preferenze
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: INFORMAZIONI PERSONALI */}
          <TabsContent value="personal">
            <Card>
              <CardHeader>
                <CardTitle>Informazioni personali</CardTitle>
                <CardDescription>Gestisci i tuoi dati professionali</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {user?.is_public_visible === false && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-1">Profilo privato</h3>
                    <p className="text-sm text-blue-800">
                      Il tuo profilo è impostato come privato e non è visibile ai membri base ordinari, ma solo al consiglio direttivo
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Nome</Label>
                    <Input id="first_name" value={user?.first_name || ''} disabled />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Cognome</Label>
                    <Input id="last_name" value={user?.last_name || ''} disabled />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label>Email corrente</Label>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input value={user?.email || ''} disabled />
                    <Button variant="outline" onClick={() => setIsEmailModalOpen(true)} className="w-full sm:w-auto">
                      Modifica
                    </Button>
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <Label>Telefono corrente</Label>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input value={`${profileData.phone_prefix} ${profileData.phone}` || 'Non impostato'} disabled />
                    <Button variant="outline" onClick={() => setIsPhoneModalOpen(true)} className="w-full sm:w-auto">
                      Modifica
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Foto profilo</Label>
                  <div className="mt-2 rounded-lg border p-4 space-y-3">
                    {profilePhotoUrl ? (
                      <AuthenticatedImage
                        src={`${getBackendMediaUrl(profilePhotoUrl)}?v=${profilePhotoVersion}`}
                        alt="Foto profilo"
                        className="h-20 w-20 rounded-full object-cover border"
                        fallback={
                          <div className="h-20 w-20 rounded-full border bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
                            Nessuna
                          </div>
                        }
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full border bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
                        Nessuna
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.gif"
                        onChange={handleProfilePhotoInputChange}
                        className="w-full sm:max-w-sm"
                        disabled={saving}
                      />
                      {profilePhotoUrl && (
                        <Button type="button" variant="outline" onClick={handleProfilePhotoDelete} disabled={saving} className="w-full sm:w-auto">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Rimuovi
                        </Button>
                      )}
                    </div>
                    {saving && <p className="text-sm text-gray-600">Caricamento in corso...</p>}
                    <p className="text-xs text-gray-500">Formati supportati: JPG, PNG, WEBP, GIF</p>
                  </div>
                </div>

                <form onSubmit={(e) => handleProfileUpdate(e, 'personal')} className="space-y-6">
                  {/* Location */}
                  <div>
                    <h3 className="font-semibold text-lg mb-4">Località</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="country">Paese *</Label>
                        <Select
                          key={`country-select-${countryOptions.length}`}
                          value={selectedCountryValue}
                          onValueChange={handleCountryChange}
                        >
                          <SelectTrigger id="country">
                            <SelectValue placeholder="Seleziona paese" />
                          </SelectTrigger>
                          <SelectContent>
                            {countryOptions.map(country => (
                              <SelectItem key={country.idValue || country.id} value={country.idValue}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="city_strgin">Città *</Label>
                        <Input
                          id="city_strgin"
                          value={profileData.city_strgin || ''}
                          onChange={(e) => setProfileData({ ...profileData, city_strgin: e.target.value })}
                          placeholder="Inserisci città"
                          required
                        />
                      </div>
                    </div>

                  </div>

                  <Button type="submit" disabled={saving || !isPersonalTabComplete}>
                    {saving ? 'Salvataggio...' : 'Salva modifiche'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: INFORMAZIONI PROFESSIONALI */}
          <TabsContent value="professional">
            <Card>
              <CardHeader>
                <CardTitle>Informazioni professionali</CardTitle>
                <CardDescription>Aggiorna i tuoi dati professionali inserendo l’azienda o l’organizzazione per cui lavori attualmente oppure l’università che stai frequentando in questo periodo (non esperienze o percorsi passati)</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => handleProfileUpdate(e, 'professional')} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company_name">Azienda / Università *</Label>
                      <Input
                        id="company_name"
                        value={profileData.company_name}
                        onChange={(e) => setProfileData({...profileData, company_name: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="job_title">Ruolo / Percorso formativo *</Label>
                      <Input
                        id="job_title"
                        value={profileData.job_title}
                        onChange={(e) => setProfileData({...profileData, job_title: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="job_category">Categoria posizione ricoperta *</Label>
                      <Select
                        value={profileData.job_category_id?.toString() || ''}
                        onValueChange={(value) => setProfileData({...profileData, job_category_id: toNullableNumber(value)})}
                        required
                      >
                        <SelectTrigger id="job_category">
                          <SelectValue placeholder="Seleziona categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id.toString()}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="company_sector">Settore Azienda / Area di riferimento formativa *</Label>
                      <Select
                        value={profileData.company_sector_id?.toString() || ''}
                        onValueChange={(value) => setProfileData({...profileData, company_sector_id: toNullableNumber(value)})}
                        required
                      >
                        <SelectTrigger id="company_sector">
                          <SelectValue placeholder="Seleziona settore" />
                        </SelectTrigger>
                        <SelectContent>
                          {companySectors.map(sector => (
                            <SelectItem key={sector.id} value={sector.id.toString()}>
                              {sector.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="linkedin">LinkedIn URL</Label>
                    <Input
                      id="linkedin"
                      type="url"
                      placeholder="https://linkedin.com/in/..."
                      value={profileData.linkedin_url}
                      onChange={(e) => setProfileData({...profileData, linkedin_url: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="additional_info">Altro (associazioni, certificazioni, studi,...)</Label>
                    <textarea
                      id="additional_info"
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Es: Membro di Associazione XYZ, Certificazione ABC, Laurea in..."
                      value={profileData.additional_info}
                      onChange={(e) => setProfileData({...profileData, additional_info: e.target.value})}
                    />
                  </div>

                  <Button type="submit" disabled={saving || !isProfessionalTabComplete}>
                    {saving ? 'Salvataggio...' : 'Salva modifiche'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: DOCUMENTI */}
          <TabsContent value="documents">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Curriculum vitae (CV)</CardTitle>
                  <CardDescription>Carica o aggiorna il tuo CV (PDF, DOC, DOCX)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasCv && (
                    <div className="flex flex-col items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 sm:flex-row sm:items-center">
                      <Check className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">CV già caricato</span>
                      <Button variant="outline" size="sm" className="w-full sm:ml-auto sm:w-auto" onClick={handleCvDownload}>
                        <Download className="h-4 w-4 mr-2" />
                        Scarica
                      </Button>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="cv-upload">Carica nuovo CV</Label>
                    <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                      <Input
                        id="cv-upload"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setCvFile(e.target.files[0])}
                      />
                      <Button onClick={handleCvUpload} disabled={!cvFile || saving} className="w-full sm:w-auto">
                        <Upload className="h-4 w-4 mr-2" />
                        Carica
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lettera motivazionale</CardTitle>
                  <CardDescription>
                    Se già presente è solo scaricabile. Se manca puoi caricarla (PDF, DOC, DOCX).
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasMotivationLetter ? (
                    <div className="flex flex-col items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 sm:flex-row sm:items-center">
                      <Check className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">Lettera motivazionale presente</span>
                      <Button variant="outline" size="sm" className="w-full sm:ml-auto sm:w-auto" onClick={handleMotivationLetterDownload}>
                        <Download className="h-4 w-4 mr-2" />
                        Scarica
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="motivation-letter-upload">Carica lettera motivazionale</Label>
                      <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                        <Input
                          id="motivation-letter-upload"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setMotivationLetterFile(e.target.files[0])}
                        />
                        <Button onClick={handleMotivationLetterUpload} disabled={!motivationLetterFile || saving} className="w-full sm:w-auto">
                          <Upload className="h-4 w-4 mr-2" />
                          Carica
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lettera di adesione</CardTitle>
                  <CardDescription>Documento gestito dall&apos;amministrazione. Da qui è solo scaricabile se presente.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasAdhesionLetter ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <Check className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">Lettera di adesione presente</span>
                      <Button variant="outline" size="sm" className="ml-auto" onClick={handleAdhesionLetterDownload}>
                        <Download className="h-4 w-4 mr-2" />
                        Scarica
                      </Button>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 border rounded-lg">
                      <p className="text-sm text-gray-600">Nessuna lettera di adesione disponibile</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB 4: QUOTE ASSOCIATIVE */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Quote associative</CardTitle>
                <CardDescription>Elenco delle tue quote personali, pagate e non pagate</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingQuotes ? (
                  <p className="text-sm text-gray-500">Caricamento quote associative...</p>
                ) : quotes.length === 0 ? (
                  <p className="text-sm text-gray-500">Nessuna quota associativa disponibile</p>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-3 sm:hidden">
                      {quotes.map((q) => (
                        <div key={q.id} className="rounded-lg border p-3">
                          <div className="mb-2">
                            <span className={`rounded px-2 py-1 text-xs ${q.pagato ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {q.pagato ? 'Pagata' : 'Non pagata'}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <p><span className="font-medium">Data inizio:</span> {formatQuoteDate(q.data_inizio)}</p>
                            <p><span className="font-medium">Data fine:</span> {formatQuoteDate(q.data_fine)}</p>
                            <p><span className="font-medium">Importo:</span> {formatQuoteAmount(q.prezzo)}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="hidden overflow-x-auto sm:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-2 pr-3 font-medium">Stato pagamento</th>
                            <th className="py-2 pr-3 font-medium">Data inizio</th>
                            <th className="py-2 pr-3 font-medium">Data fine</th>
                            <th className="py-2 pr-3 font-medium">Importo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotes.map((q) => (
                            <tr key={q.id} className="border-b last:border-b-0">
                              <td className="py-3 pr-3">
                                <span className={`rounded px-2 py-1 text-xs ${q.pagato ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                  {q.pagato ? 'Pagata' : 'Non pagata'}
                                </span>
                              </td>
                              <td className="py-3 pr-3">{formatQuoteDate(q.data_inizio)}</td>
                              <td className="py-3 pr-3">{formatQuoteDate(q.data_fine)}</td>
                              <td className="py-3 pr-3 font-medium">{formatQuoteAmount(q.prezzo)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 5: PRIVACY E PREFERENZE */}
          <TabsContent value="privacy">
            <div className="space-y-6">
              {/* Informative Privacy e Cookie */}
              <Card>
                <CardHeader>
                  <CardTitle>Privacy e Cookie</CardTitle>
                  <CardDescription>Informazioni sul trattamento dei dati e utilizzo dei cookie</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Cookie Policy */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">🍪 Cookie Policy</h3>
                    <p className="text-sm text-blue-800 mb-3">
                      Il presente sito utilizza esclusivamente cookie tecnici, necessari al corretto 
                      funzionamento delle pagine e a garantire una navigazione sicura ed efficiente.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.location.href = '/cookie-policy'}
                      className="border-blue-600 text-blue-600 hover:bg-blue-100"
                    >
                      Leggi la Cookie Policy completa →
                    </Button>
                  </div>

                  {/* Privacy Policy GDPR */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">🔒 Privacy Policy</h3>
                    <p className="text-sm text-green-800 mb-3">
                      Privacy Policy ai sensi dell&apos;art. 13 Reg. UE 2016/679 (GDPR)
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.location.href = '/privacy-policy'}
                      className="border-green-600 text-green-600 hover:bg-green-100"
                    >
                      Leggi la Privacy Policy completa →
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Notifiche */}
              <Card>
                <CardHeader>
                  <CardTitle>Preferenze notifiche</CardTitle>
                  <CardDescription>Scegli quali notifiche ricevere</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingConsents && (
                    <p className="text-sm text-gray-500">Caricamento preferenze...</p>
                  )}

                  {!loadingConsents && consents.length === 0 && (
                    <p className="text-sm text-gray-500">Nessun consenso configurato</p>
                  )}

                  {!loadingConsents && consents.map((consent) => (
                    <div key={consent.id} className="flex items-start gap-3">
                      <Checkbox
                        id={`consent-${consent.id}`}
                        checked={!!consentValues[consent.id]}
                        onCheckedChange={(checked) =>
                          setConsentValues((prev) => ({
                            ...prev,
                            [consent.id]: checked === true
                          }))
                        }
                        disabled={savingConsents}
                      />
                      <div>
                        <Label htmlFor={`consent-${consent.id}`} className="font-medium">
                          {consent.title}
                        </Label>
                        {consent.description && (
                          <p className="text-sm text-gray-500">
                            {consent.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSaveConsents}
                      disabled={loadingConsents || savingConsents || consents.length === 0}
                    >
                      {savingConsents ? 'Salvataggio...' : 'Salva preferenze notifiche'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Phone Change Modal */}
      <Dialog open={isPhoneModalOpen} onOpenChange={setIsPhoneModalOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica telefono</DialogTitle>
            <DialogDescription>
              Inserisci il nuovo numero di telefono. Riceverai un codice OTP per conferma.
            </DialogDescription>
          </DialogHeader>
          
          {!phoneOtpSent ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <Label>Prefisso</Label>
                  <Select value={newPhonePrefix} onValueChange={setNewPhonePrefix}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_PREFIXES.map(prefix => (
                        <SelectItem key={prefix.code} value={prefix.code}>
                          {prefix.flag} {prefix.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Numero</Label>
                  <Input
                    type="tel"
                    placeholder="3331234567"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsPhoneModalOpen(false)} className="w-full sm:w-auto">
                  Annulla
                </Button>
                <Button onClick={handleRequestPhoneOtp} disabled={sendingPhoneOtp} className="w-full sm:w-auto">
                  {sendingPhoneOtp ? 'Invio...' : 'Invia OTP'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Codice OTP (6 cifre)</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="123456"
                  value={phoneOtpCode}
                  onChange={(e) => setPhoneOtpCode(e.target.value.replace(/\D/g, ''))}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Inserisci il codice ricevuto via SMS
                </p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setPhoneOtpSent(false);
                  setPhoneOtpCode('');
                }} className="w-full sm:w-auto">
                  Indietro
                </Button>
                <Button onClick={handleVerifyPhoneOtp} disabled={verifyingPhone} className="w-full sm:w-auto">
                  {verifyingPhone ? 'Verifica...' : 'Verifica OTP'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProfilePhotoCropModal
        open={cropModalOpen}
        onOpenChange={(open) => {
          setCropModalOpen(open);
          if (!open) {
            setRawProfilePhotoFile(null);
          }
        }}
        file={rawProfilePhotoFile}
        onConfirm={handleCropConfirm}
      />

      {/* Email Change Modal */}
      <Dialog open={isEmailModalOpen} onOpenChange={setIsEmailModalOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica email</DialogTitle>
            <DialogDescription>
              Inserisci la nuova email. Riceverai un codice OTP per conferma.
            </DialogDescription>
          </DialogHeader>
          
          {!emailOtpSent ? (
            <div className="space-y-4">
              <div>
                <Label>Nuova Email</Label>
                <Input
                  type="email"
                  placeholder="nuova@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEmailModalOpen(false)} className="w-full sm:w-auto">
                  Annulla
                </Button>
                <Button onClick={handleRequestEmailOtp} disabled={sendingEmailOtp} className="w-full sm:w-auto">
                  {sendingEmailOtp ? 'Invio...' : 'Invia OTP'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Codice OTP (6 cifre)</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="123456"
                  value={emailOtpCode}
                  onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, ''))}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Inserisci il codice ricevuto via email
                </p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setEmailOtpSent(false);
                  setEmailOtpCode('');
                }} className="w-full sm:w-auto">
                  Indietro
                </Button>
                <Button onClick={handleVerifyEmailOtp} disabled={verifyingEmail} className="w-full sm:w-auto">
                  {verifyingEmail ? 'Verifica...' : 'Verifica OTP'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
